import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import Paginations from "components/Common/Paginations"
import DeleteModal from "components/Common/DeleteModal"
import ConfirmModal from "components/Common/ConfirmModal"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import {
  saGetAllPlans,
  saDeletePlan,
  saUpdatePlanStatus,
} from "store/actions/superAdminAction"
import { getCurrencyLabel } from "utils/currency"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import isPermissionEnabled from "common/isPermissionEnabled"
import { translateBillingCycle, translateStatusLabel } from "utils/statusLabels"
import { countPlanModules } from "constants/planPermissionMap"

const PlansList = () => {
  const { t } = useTranslation()
  document.title = t("Plans | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const pageNumber = parseInt(new URLSearchParams(location.search).get("page")) || 1

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewPlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_1",
    accountType,
    accountScope
  )
  const canCreatePlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_2",
    accountType,
    accountScope
  )
  const canEditPlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_3",
    accountType,
    accountScope
  )
  const canDeletePlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_4",
    accountType,
    accountScope
  )
  const hasRowActions = canViewPlans || canEditPlans || canDeletePlans

  const { plansLoading, planActionLoading, plans, plansTotalCount, plansTotalPages } =
    useSelector(state => state.SuperAdmin)

  const [deleteModal, setDeleteModal] = useState({ open: false, id: null })
  const [statusModal, setStatusModal] = useState({
    open: false,
    plan: null,
  })

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canViewPlans) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(saGetAllPlans({ page: pageNumber, limit: 10 }))
  }, [dispatch, pageNumber, isSuperAdminSession, canViewPlans, navigate])

  const handlePageChange = page => {
    const params = new URLSearchParams(location.search)
    params.set("page", page.toString())
    navigate(`${location.pathname}?${params.toString()}`)
  }

  const handleDelete = () => {
    if (!deleteModal.id) return
    dispatch(saDeletePlan({ id: deleteModal.id })).then(res => {
      if (res?.payload?.status) {
        dispatch(
          showAlert({ type: "success", message: t("Plan deleted successfully") })
        )
      } else {
        dispatch(
          showAlert({
            type: "danger",
            message:
              res?.payload?.msg ||
              t("Failed to delete plan. Please try again."),
          })
        )
      }
      setDeleteModal({ open: false, id: null })
    })
  }

  const confirmStatusChange = () => {
    const { plan } = statusModal
    if (!plan?._id) {
      setStatusModal({ open: false, plan: null })
      return
    }
    const nextStatus = plan.status === "active" ? "inactive" : "active"
    dispatch(saUpdatePlanStatus({ id: plan._id, status: nextStatus })).then(
      res => {
        if (res?.payload?.status) {
          dispatch(
            showAlert({
              type: "success",
              message: t("Plan status updated successfully"),
            })
          )
          setStatusModal({ open: false, plan: null })
        } else {
          dispatch(
            showAlert({
              type: "danger",
              message:
                res?.payload?.msg ||
                t("Failed to update plan status. Please try again."),
            })
          )
        }
      }
    )
  }

  const getPlanActionItems = plan => [
    ...(canViewPlans
      ? [
          {
            key: "view",
            content: (
              <>
                <i className="bx bx-show me-1" />
                {t("View")}
              </>
            ),
            onClick: () => navigate(`${ROUTES.SA_PLAN_VIEW}?id=${plan._id}`),
          },
        ]
      : []),
    ...(canEditPlans
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="bx bx-edit me-1" />
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`${ROUTES.SA_PLAN_EDIT}?id=${plan._id}`),
          },
          {
            key: "status",
            content: (
              <>
                <i
                  className={`bx ${
                    plan.status === "active" ? "bx-pause" : "bx-play"
                  } me-1`}
                />
                {plan.status === "active" ? t("Deactivate") : t("Activate")}
              </>
            ),
            disabled: planActionLoading,
            onClick: () => setStatusModal({ open: true, plan }),
          },
        ]
      : []),
    ...(canDeletePlans
      ? [
          {
            key: "delete",
            className: "text-danger",
            content: (
              <>
                <i className="bx bx-trash me-1" />
                {t("Delete")}
              </>
            ),
            onClick: () => setDeleteModal({ open: true, id: plan._id }),
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="mb-0">{t("Subscription Plans")}</h4>
              <p className="text-muted mb-0">
                {t("Create and manage subscription plans")}
              </p>
            </Col>
            <Col xs="auto">
              {canCreatePlans && (
              <Button
                color="primary"
                onClick={() => navigate(ROUTES.SA_PLAN_ADD)}
              >
                <i className="bx bx-plus me-1" />
                {t("Create Plan")}
              </Button>
              )}
            </Col>
          </Row>

          <Card>
            <CardBody>
              {plansLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : plans.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {t("No plans found. Create your first plan.")}
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("Plan Name")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Billing Cycle")}</th>
                          <th>{t("Price")}</th>
                          <th>{t("Modules")}</th>
                          <th>{t("Add-ons")}</th>
                          <th>{t("Status")}</th>
                          <th style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans.map((plan, idx) => (
                          <tr key={plan._id || idx}>
                            <td>
                              <div className="fw-medium">{plan.name}</div>
                            </td>
                            <td>
                              <Badge
                                color={
                                  plan.planType === "enterprise" ? "info" : "secondary"
                                }
                              >
                                {plan.planType === "enterprise"
                                  ? t("Enterprise")
                                  : t("Normal")}
                              </Badge>
                            </td>
                            <td>
                              {translateBillingCycle(t, plan.billingCycle)}
                            </td>
                            <td>
                              {getCurrencyLabel(plan.currency || "SAR")}{" "}
                              {Number(plan.price || 0).toFixed(2)}
                            </td>
                            <td>{countPlanModules(plan) || "—"}</td>
                            <td>
                              {Array.isArray(plan.addOns)
                                ? plan.addOns.length
                                : "—"}
                            </td>
                            <td>
                              <Badge
                                color={plan.status === "active" ? "success" : "secondary"}
                              >
                                {plan.status === "active" ? t("Active") : t("Inactive")}
                              </Badge>
                            </td>
                            <td>
                              <div className="gap-2 d-flex align-items-center">
                                {hasRowActions && (
                                <TableActionsDropdown items={getPlanActionItems(plan)} />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                  </Table>
                  </div>

                  <Paginations
                    currentPage={pageNumber}
                    totalPages={plansTotalPages}
                    onPageChange={handlePageChange}
                    totalCount={plansTotalCount}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <DeleteModal
        show={deleteModal.open}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal({ open: false, id: null })}
        isLoading={planActionLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this plan?")}
      />

      <ConfirmModal
        show={statusModal.open}
        onCloseClick={() => setStatusModal({ open: false, plan: null })}
        onDeleteClick={confirmStatusChange}
        isLoading={planActionLoading}
        buttonText={
          statusModal.plan?.status === "active"
            ? t("Deactivate")
            : t("Activate")
        }
        bodyTitle={
          statusModal.plan?.status === "active"
            ? t("Are you sure you want to deactivate this plan?")
            : t("Are you sure you want to activate this plan?")
        }
      />
    </React.Fragment>
  )
}

export default PlansList
